"use client";

import { Fragment, useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { apiFetch } from "@/src/lib/api-fetch";
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Lock,
  Plus,
  Trash2,
  X,
  ClipboardPaste,
} from "lucide-react";
import { safeParseLessonContent } from "@/lib/lesson-content/schema";
import {
  BLOCK_MENU,
  blockLabel,
  makeBlock,
  type BlockMenuItem,
  makeSection,
  type EditorBlock,
  type EditorSection,
} from "./blockDefaults";
import { BlockFields } from "./BlockFields";
import IleAdminPreview from "./IleAdminPreview";
import { SortableList } from "./Sortable";
import ImportJsonModal from "./ImportJsonModal";

type Props = { lessonId: string };

/** Which item (if any) the edit panel is bound to. Tracked by id so it survives reorders. */
type Selection = { kind: "block"; blockId: string } | { kind: "section"; sectionId: string } | null;

/** A validation problem, made human-readable and linkable to the offending block/section. */
type Issue = { text: string; sectionId?: string; blockId?: string };

export default function LessonCanvas({ lessonId }: Props) {
  const [sections, setSections] = useState<EditorSection[]>([]);
  const [title, setTitle] = useState<string>("");
  // Comma-separated in the input; saved as string[] in the content envelope.
  const [keyConcepts, setKeyConcepts] = useState<string>("");
  // Intro header shown on the first part. Objectives are one per line in the textarea.
  const [subtitle, setSubtitle] = useState<string>("");
  const [objectivesText, setObjectivesText] = useState<string>("");
  const [courseId, setCourseId] = useState<number | null>(null);
  const [selected, setSelected] = useState<Selection>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [issues, setIssues] = useState<Issue[]>([]);
  const [mode, setMode] = useState<"edit" | "preview">("edit");
  const [showImport, setShowImport] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await apiFetch(`/api/admin/lessons/${lessonId}/content`);
        if (!res.ok) throw new Error(`Failed to load lesson (${res.status})`);
        const data = await res.json();
        if (!active) return;
        const loaded = data?.content?.sections;
        if (Array.isArray(loaded)) setSections(loaded as EditorSection[]);
        const intro = data?.content?.intro;
        if (typeof intro?.subtitle === "string") setSubtitle(intro.subtitle);
        if (Array.isArray(intro?.objectives)) setObjectivesText((intro.objectives as string[]).join("\n"));
        if (Array.isArray(data?.lesson?.key_concepts)) {
          setKeyConcepts((data.lesson.key_concepts as string[]).join(", "));
        }
        if (data?.lesson?.title) setTitle(String(data.lesson.title));
        if (typeof data?.lesson?.course_id === "number") setCourseId(data.lesson.course_id);
      } catch (e) {
        if (active) setError(e instanceof Error ? e.message : "Failed to load lesson");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [lessonId]);

  const intro = useMemo(() => {
    const objectives = objectivesText.split("\n").map((o) => o.trim()).filter(Boolean);
    const sub = subtitle.trim();
    if (!sub && objectives.length === 0) return undefined;
    return { ...(sub ? { subtitle: sub } : {}), ...(objectives.length ? { objectives } : {}) };
  }, [subtitle, objectivesText]);

  const blockCount = useMemo(
    () => sections.reduce((n, s) => n + s.blocks.length, 0),
    [sections],
  );

  const selectedSection: EditorSection | undefined =
    selected?.kind === "section"
      ? sections.find((s) => s.id === selected.sectionId)
      : undefined;
  const selectedBlock: EditorBlock | undefined =
    selected?.kind === "block"
      ? sections.flatMap((s) => s.blocks).find((b) => b.id === selected.blockId)
      : undefined;

  // ── section + block mutations (by id, so chain reorders don't break them) ──
  const patchSection = (id: string, patch: Partial<EditorSection>) =>
    setSections((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));

  const setSectionBlocks = (id: string, blocks: EditorBlock[]) =>
    setSections((prev) => prev.map((s) => (s.id === id ? { ...s, blocks } : s)));

  const moveSection = (index: number, dir: -1 | 1) => {
    const j = index + dir;
    if (j < 0 || j >= sections.length) return;
    setSections((prev) => {
      const next = [...prev];
      [next[index], next[j]] = [next[j], next[index]];
      return next;
    });
  };

  const addSection = () => {
    const sec = makeSection(crypto.randomUUID());
    setSections((prev) => [...prev, sec]);
    setSelected({ kind: "section", sectionId: sec.id });
  };

  const removeSection = (id: string) => {
    setSections((prev) => prev.filter((s) => s.id !== id));
    setSelected((cur) => (cur?.kind === "section" && cur.sectionId === id ? null : cur));
  };

  const addBlock = (sectionId: string, item: BlockMenuItem) => {
    const block = makeBlock(item.type, crypto.randomUUID(), item.data);
    setSections((prev) =>
      prev.map((s) => (s.id === sectionId ? { ...s, blocks: [...s.blocks, block] } : s)),
    );
    setSelected({ kind: "block", blockId: block.id });
  };

  const updateBlockData = (blockId: string, patch: Record<string, unknown>) =>
    setSections((prev) =>
      prev.map((s) => ({
        ...s,
        blocks: s.blocks.map((b) => (b.id === blockId ? { ...b, data: { ...b.data, ...patch } } : b)),
      })),
    );

  const removeBlock = (blockId: string) => {
    setSections((prev) => prev.map((s) => ({ ...s, blocks: s.blocks.filter((b) => b.id !== blockId) })));
    setSelected((cur) => (cur?.kind === "block" && cur.blockId === blockId ? null : cur));
  };

  const dismissIssue = (idx: number) => {
    const next = issues.filter((_, i) => i !== idx);
    setIssues(next);
    if (next.length === 0) setError(null);
  };

  const save = async () => {
    setError(null);
    setSuccess(null);
    setIssues([]);

    const concepts = keyConcepts.split(",").map((c) => c.trim()).filter(Boolean);
    const parsed = safeParseLessonContent({ version: 1, format: "blocks", sections, intro });
    if (!parsed.success) {
      setIssues(parsed.error.issues.map((iss) => describeIssue(iss.path, iss.message, sections)));
      setError("Fix the highlighted issues before saving.");
      return;
    }

    setSaving(true);
    try {
      const res = await apiFetch(`/api/admin/lessons/${lessonId}/content`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content: parsed.data, key_concepts: concepts }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (Array.isArray(body?.issues)) {
          setIssues(
            body.issues.map((iss: { path?: (string | number)[]; message?: string }) =>
              describeIssue(iss.path ?? [], iss.message ?? "Invalid value", sections),
            ),
          );
        }
        throw new Error(body?.error || `Save failed (${res.status})`);
      }
      setSuccess("Saved.");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const backHref = courseId != null ? `/admin/courses/builder?course=${courseId}` : "/admin/courses/builder";

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-8 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading lesson…
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {showImport && (
        <ImportJsonModal
          hasExistingContent={sections.length > 0}
          onClose={() => setShowImport(false)}
          onImport={(imported, importedIntro) => {
            setSections(imported);
            // Replace, never merge: JSON without an intro clears the previous lesson's.
            setSubtitle(importedIntro?.subtitle ?? "");
            setObjectivesText((importedIntro?.objectives ?? []).join("\n"));
            setSelected(null);
            setIssues([]);
            setError(null);
            setShowImport(false);
            setSuccess(
              `Imported ${imported.length} section${imported.length === 1 ? "" : "s"} — review below, then Save.`,
            );
          }}
        />
      )}
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <Link
            href={backHref}
            className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-800"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to structure
          </Link>
          <h1 className="truncate text-xl font-bold text-slate-900">{title || "Lesson builder"}</h1>
          <p className="text-xs text-slate-500">
            Lesson #{lessonId} • {sections.length} section{sections.length === 1 ? "" : "s"} • {blockCount} block
            {blockCount === 1 ? "" : "s"}
          </p>
          <p className="mt-1 text-xs text-slate-400">
            Add sections, drop blocks into each, then click a card to edit it in the panel below.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center rounded-lg border border-slate-200 bg-white p-0.5">
            {(["edit", "preview"] as const).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setMode(m)}
                className={`rounded-md px-3 py-1.5 text-sm font-semibold capitalize transition-colors ${
                  mode === m ? "bg-indigo-600 text-white" : "text-slate-600 hover:text-slate-900"
                }`}
              >
                {m}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setShowImport(true)}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50"
          >
            <ClipboardPaste className="h-4 w-4" /> Import JSON
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Save
          </button>
        </div>
      </div>

      <section className="rounded-xl border border-slate-200 bg-white p-4">
        <h2 className="mb-3 text-sm font-bold text-slate-900">Lesson details</h2>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="flex flex-col gap-4">
            <DetailField
              label="Subtitle"
              hint="One line under the lesson title, on the first part. Optional."
            >
              <input
                value={subtitle}
                onChange={(e) => setSubtitle(e.target.value)}
                placeholder="What AI actually is, how it learns from examples, and the three jobs it does at work."
                className={detailInputClass}
              />
            </DetailField>
            <DetailField
              label="Key concepts"
              hint="Comma-separated. What this lesson teaches; used to personalize scenarios."
            >
              <input
                value={keyConcepts}
                onChange={(e) => setKeyConcepts(e.target.value)}
                placeholder="Pattern recognition, Prediction, Assistance"
                className={detailInputClass}
              />
            </DetailField>
          </div>
          <DetailField
            label="Objectives"
            hint={"One per line. Shown as \u201cBy the end, you\u2019ll be able to\u201d on the first part. Optional."}
          >
            <textarea
              value={objectivesText}
              onChange={(e) => setObjectivesText(e.target.value)}
              rows={5}
              placeholder={"Explain what AI is in plain words\nTell apart AI's three jobs"}
              className={`${detailInputClass} resize-y`}
            />
          </DetailField>
        </div>
      </section>

      {error && (
        <div className="relative rounded-lg border border-rose-200 bg-rose-50 p-3 pr-9 text-sm text-rose-800">
          <button
            type="button"
            onClick={() => {
              setError(null);
              setIssues([]);
            }}
            title="Dismiss"
            className="absolute right-2 top-2 rounded p-1 text-rose-400 hover:bg-rose-100 hover:text-rose-700"
          >
            <X className="h-4 w-4" />
          </button>
          {error}
          {issues.length > 0 && (
            <ul className="mt-2 space-y-1 text-xs">
              {issues.map((iss, idx) => (
                <li key={idx} className="flex items-start gap-1.5">
                  <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-rose-400" />
                  {iss.blockId || iss.sectionId ? (
                    <button
                      type="button"
                      onClick={() => {
                        setMode("edit");
                        if (iss.blockId) setSelected({ kind: "block", blockId: iss.blockId });
                        else if (iss.sectionId) setSelected({ kind: "section", sectionId: iss.sectionId });
                      }}
                      className="flex-1 text-left underline decoration-rose-300 underline-offset-2 hover:text-rose-900"
                      title="Go to this block"
                    >
                      {iss.text}
                    </button>
                  ) : (
                    <span className="flex-1">{iss.text}</span>
                  )}
                  <button
                    type="button"
                    onClick={() => dismissIssue(idx)}
                    title="Dismiss this issue"
                    className="shrink-0 rounded p-0.5 text-rose-400 hover:bg-rose-100 hover:text-rose-700"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {success && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
          {success}
        </div>
      )}

      {mode === "preview" ? (
        <IleAdminPreview draft={{ version: 1, format: "blocks", sections, intro }} lessonTitle={title} />
      ) : (
        <>
          {/* The chain: sections as inline groups, blocks as cards inside each */}
          <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
            {sections.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500">
                No sections yet. Add the first part to start building.
              </div>
            ) : (
              <div className="flex flex-wrap items-start gap-3">
                {sections.map((section, si) => (
                  <Fragment key={section.id}>
                    <div
                      className={`flex items-center gap-2 rounded-xl border bg-white p-2 shadow-sm transition-colors ${
                        selected?.kind === "section" && selected.sectionId === section.id
                          ? "border-indigo-400 ring-2 ring-indigo-100"
                          : "border-slate-200"
                      }`}
                    >
                      {/* Section chip */}
                      <button
                        type="button"
                        onClick={() => setSelected({ kind: "section", sectionId: section.id })}
                        className="flex items-center gap-1.5 rounded-lg bg-slate-900 px-2.5 py-1.5 text-left text-xs font-bold uppercase tracking-wide text-white"
                        title="Edit this section"
                      >
                        <span className="opacity-60">§{si + 1}</span>
                        <span className="max-w-[10rem] truncate normal-case">
                          {section.title?.trim() || `Part ${si + 1}`}
                        </span>
                        {section.required_to_advance && <Lock className="h-3 w-3 opacity-70" />}
                      </button>

                      {/* Section reorder */}
                      <div className="flex items-center">
                        <button
                          type="button"
                          onClick={() => moveSection(si, -1)}
                          disabled={si === 0}
                          title="Move section left"
                          className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30"
                        >
                          <ChevronLeft className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => moveSection(si, 1)}
                          disabled={si === sections.length - 1}
                          title="Move section right"
                          className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30"
                        >
                          <ChevronRight className="h-3.5 w-3.5" />
                        </button>
                      </div>

                      {/* This section's block cards (drag to reorder within the section) */}
                      <SortableList
                        items={section.blocks}
                        onReorder={(next) => setSectionBlocks(section.id, next)}
                        orientation="horizontal"
                        className="flex flex-wrap items-center gap-2"
                        render={(block, handle) => (
                          <BlockCard
                            block={block}
                            handle={handle}
                            selected={selected?.kind === "block" && selected.blockId === block.id}
                            onSelect={() => setSelected({ kind: "block", blockId: block.id })}
                          />
                        )}
                      />

                      <AddBlockButton onAdd={(item) => addBlock(section.id, item)} />
                    </div>
                  </Fragment>
                ))}
              </div>
            )}

            <button
              type="button"
              onClick={addSection}
              className="mt-3 inline-flex items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-100"
            >
              <Plus className="h-4 w-4" /> Add section
            </button>
          </div>

          {/* The single edit panel — only ever shows the selected item, so it never grows downward */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            {selectedBlock ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between gap-3 border-b border-slate-100 pb-3">
                  <h2 className="text-sm font-bold text-slate-900">
                    Editing: <span className="text-indigo-700">{blockLabel(selectedBlock)}</span>
                  </h2>
                  <button
                    type="button"
                    onClick={() => removeBlock(selectedBlock.id)}
                    className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold text-rose-600 hover:bg-rose-50"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Delete block
                  </button>
                </div>
                <BlockFields
                  key={selectedBlock.id}
                  block={selectedBlock}
                  index={0}
                  updateData={(_, patch) => updateBlockData(selectedBlock.id, patch)}
                />
              </div>
            ) : selectedSection ? (
              <SectionPanel
                section={selectedSection}
                onChange={(patch) => patchSection(selectedSection.id, patch)}
                onDelete={() => removeSection(selectedSection.id)}
              />
            ) : (
              <p className="py-6 text-center text-sm text-slate-400">
                Select a section chip or a block card above to edit it here.
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ── Turn a raw Zod issue path into a human-readable, linkable message ───────

/**
 * Maps a validation issue (path like ["sections",0,"blocks",2,"data","hotspots",0,"title"])
 * to a message that names the part + block ("Part 1 ▸ Image hotspot (block 3): …") and
 * carries the section/block ids so the error can be clicked to jump to that block.
 */
function describeIssue(
  path: (string | number)[],
  message: string,
  sections: EditorSection[],
): Issue {
  if (path[0] === "sections" && typeof path[1] === "number") {
    const si = path[1];
    const section = sections[si];
    const secName = section?.title?.trim()
      ? `Part ${si + 1} “${section.title.trim()}”`
      : `Part ${si + 1}`;

    if (path[2] === "blocks" && typeof path[3] === "number") {
      const bi = path[3];
      const block = section?.blocks[bi];
      const blockName = block ? blockLabel(block) : "block";
      const rest = path.slice(path[4] === "data" ? 5 : 4);
      const field = rest.length ? ` — ${rest.join(".")}` : "";
      return {
        text: `${secName} ▸ ${blockName} (block ${bi + 1})${field}: ${message}`,
        sectionId: section?.id,
        blockId: block?.id,
      };
    }
    return { text: `${secName}: ${message}`, sectionId: section?.id };
  }

  if (path.length === 1 && path[0] === "sections") {
    return { text: "Add at least one section before saving." };
  }
  return { text: `${path.join(".") || "(root)"}: ${message}` };
}

// ── A single block card in the chain ────────────────────────────────────────

function BlockCard({
  block,
  handle,
  selected,
  onSelect,
}: {
  block: EditorBlock;
  handle: React.ReactNode;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <div
      className={`flex h-12 items-center gap-1 rounded-lg border bg-white pl-1 transition-colors ${
        selected ? "border-indigo-400 ring-2 ring-indigo-100" : "border-slate-200 hover:border-slate-300"
      }`}
    >
      {handle}
      <button
        type="button"
        onClick={onSelect}
        className={`flex h-full max-w-[12rem] flex-col justify-center py-1 pr-3 text-left ${
          selected ? "text-indigo-700" : "text-slate-700"
        }`}
      >
        <span className="truncate text-xs font-semibold leading-tight">{blockLabel(block)}</span>
        {blockSnippet(block) && (
          <span className="max-w-full truncate text-[11px] leading-tight text-slate-400">
            {blockSnippet(block)}
          </span>
        )}
      </button>
    </div>
  );
}

/** Short preview text for a block card, when the block has obvious lead text. */
function blockSnippet(block: EditorBlock): string {
  const d = block.data as Record<string, unknown>;
  const pick = (v: unknown) => (typeof v === "string" ? v.trim() : "");
  switch (block.type) {
    case "rich_text":
      return pick(d.markdown).replace(/[#*`>_]/g, "").slice(0, 36);
    case "image":
      return pick(d.alt) || pick(d.caption);
    case "mcq":
      return pick(d.question);
    case "fill_blank":
      return pick(d.template).slice(0, 36);
    default:
      return "";
  }
}

// ── "+ block" type picker (popover) ─────────────────────────────────────────

function AddBlockButton({ onAdd }: { onAdd: (item: BlockMenuItem) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title="Add a block to this section"
        className="inline-flex items-center gap-1 rounded-lg border border-dashed border-slate-300 px-2.5 py-1.5 text-xs font-semibold text-slate-500 hover:border-indigo-300 hover:text-indigo-700"
      >
        <Plus className="h-3.5 w-3.5" /> Block
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-full z-20 mt-1 grid max-h-60 w-60 grid-cols-1 gap-0.5 overflow-y-auto rounded-lg border border-slate-200 bg-white p-1.5 shadow-lg">
            {BLOCK_MENU.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => {
                  onAdd(item);
                  setOpen(false);
                }}
                className="rounded-md px-2.5 py-1.5 text-left text-sm text-slate-700 hover:bg-indigo-50 hover:text-indigo-700"
              >
                {item.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── Section settings panel ──────────────────────────────────────────────────

function SectionPanel({
  section,
  onChange,
  onDelete,
}: {
  section: EditorSection;
  onChange: (patch: Partial<EditorSection>) => void;
  onDelete: () => void;
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 border-b border-slate-100 pb-3">
        <h2 className="text-sm font-bold text-slate-900">Editing: section</h2>
        <button
          type="button"
          onClick={onDelete}
          className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold text-rose-600 hover:bg-rose-50"
        >
          <Trash2 className="h-3.5 w-3.5" /> Delete section
        </button>
      </div>
      <label className="block">
        <span className="mb-1 block text-xs font-semibold text-slate-600">Section title (optional)</span>
        <input
          value={section.title}
          onChange={(e) => onChange({ title: e.target.value })}
          placeholder="e.g. Introduction"
          className="w-full rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-800 focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-400"
        />
      </label>
      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input
          type="checkbox"
          checked={section.required_to_advance}
          onChange={(e) => onChange({ required_to_advance: e.target.checked })}
          className="rounded border-slate-300"
        />
        Required to advance — student must complete this part’s interactive blocks before “Next”
      </label>
    </div>
  );
}

const detailInputClass =
  "w-full rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-800 focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-400";

function DetailField({ label, hint, children }: { label: string; hint: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-semibold text-slate-700">{label}</span>
      {children}
      <span className="text-[11px] text-slate-400">{hint}</span>
    </label>
  );
}
